import "./style.scss";
import { useMemo, useState } from "react";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import ArrowUpSvg from "@/src/svg/CustomUtils/ArrowUpSvg";
import EditorSvg from "@/src/svg/CustomUtils/EditorSvg";
import SearchSvg from "@/src/svg/CustomUtils/SearchSvg";

export type CustomSingleSelectStatusOption = {
  label: string;
  id: string;
};

export type CustomSingleSelectGroup = {
  title: string;
  items: string[];
};

type Props = {
  title?: string;
  data?: string[];
  groups?: CustomSingleSelectGroup[];
  isSearch?: boolean;
  allowAdd?: boolean;
  columnValue: string;
  onChange: (value: string) => void;
  onClose: () => void;
  onAddItem?: (value: string) => void;
  statusOptions?: CustomSingleSelectStatusOption[];
};

const CustomSingleSelect = ({
  title,
  data = [],
  groups,
  isSearch = false,
  allowAdd = false,
  columnValue,
  onChange,
  onClose,
  onAddItem,
  statusOptions,
}: Props) => {
  const [search, setSearch] = useState("");
  const [isAddInputOpen, setIsAddInputOpen] = useState(false);
  const [addInputValue, setAddInputValue] = useState("");

  const filteredData = useMemo(() => {
    if (!isSearch || !search.trim()) return data;
    return data.filter((item) => item.toLowerCase().includes(search.toLowerCase()));
  }, [search, isSearch, data]);

  const filteredGroups = useMemo(() => {
    if (!groups?.length) return [];

    if (!isSearch || !search.trim()) {
      return groups;
    }

    const query = search.toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.toLowerCase().includes(query)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, isSearch, search]);

  const isGroupedMode = filteredGroups.length > 0;

  const handleCancel = () => {
    if (isSearch) setSearch("");
    onClose();
  };

  const handleSelectItem = (itemValue: string) => {
    onChange(itemValue);
    if (isSearch) setSearch("");
    onClose();
  };

  const handleAddButtonClick = () => {
    setIsAddInputOpen(true);
  };

  const handleAddInputClose = () => {
    setAddInputValue("");
    setIsAddInputOpen(false);
  };

  const handleAddInputSave = () => {
    const trimmed = addInputValue.trim();
    if (trimmed && allowAdd && onAddItem) {
      onAddItem(trimmed);
    }
    setAddInputValue("");
    setIsAddInputOpen(false);
  };

  const isStatusMode = Boolean(statusOptions?.length);

  return (
    <div
      className={`custom-single-select${title ? "" : " custom-single-select--no-title"}${isGroupedMode ? " custom-single-select--grouped" : ""}`}
    >
      {title && (
        <div className="custom-single-select-title">
          <div className="custom-single-select-title-text">
            <p>{title}</p>
          </div>
          <div className="custom-single-select-title-close" onClick={handleCancel}>
            <CloseSvg />
          </div>
        </div>
      )}
      {isSearch && (
        <div className="custom-single-select-search">
          <div className="custom-single-select-search-svg">
            <SearchSvg />
          </div>
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      )}
      <div className="custom-single-select-list">
        <ul className="custom-single-select-ul">
          {isStatusMode
            ? statusOptions!.map(({ id, label }) => (
                <li key={id} className="custom-single-select-li" onClick={() => handleSelectItem(label)}>
                  <p className={id}>{label}</p>
                </li>
              ))
            : isGroupedMode
              ? filteredGroups.map((group) => (
                  <li key={group.title || group.items[0]} className="custom-single-select-group">
                    {group.title ? (
                      <div className="custom-single-select-group-title">
                        <p>{group.title}</p>
                      </div>
                    ) : null}
                    <ul className="custom-single-select-group-items">
                      {group.items.map((item) => (
                        <li key={item} className="custom-single-select-li" onClick={() => handleSelectItem(item)}>
                          <p>{item}</p>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))
              : filteredData.map((item, index) => (
                  <li key={`${item}-${index}`} className="custom-single-select-li" onClick={() => handleSelectItem(item)}>
                    <p>{item}</p>
                  </li>
                ))}
        </ul>
      </div>
      {allowAdd && (
        <div className="custom-single-select-add">
          {!isAddInputOpen && (
            <div className="custom-single-select-add-button" onClick={handleAddButtonClick}>
              <div className="custom-single-select-add-button-text">
                <p>Add option</p>
              </div>
              <div className="custom-single-select-add-button-svg">
                <EditorSvg />
              </div>
            </div>
          )}
          {isAddInputOpen && (
            <div className="custom-single-select-add-input">
              <input type="text" value={addInputValue} onChange={(e) => setAddInputValue(e.target.value)} placeholder="Add option" />
              <div className="custom-single-select-add-input-buttons">
                <div className="custom-single-select-add-input-buttons-save" onClick={handleAddInputSave}>
                  <ArrowUpSvg />
                </div>
                <div className="custom-single-select-add-input-buttons-close" onClick={handleAddInputClose}>
                  <CloseSvg />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSingleSelect;
